import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { GuideConfig, GuideResultData } from '@/data/types';
import { getGuideConfig } from '@/data/categoryGuides';
import { suitUpgradeGuides, suitResultCheckPoints, getUpgradeResult, type UpgradeGuideData } from '@/data/suitResultExtras';
import { getPartnerShopList } from '@/api/partnerShops';
import type { PartnerShopListItem } from '@/types/partnerShop';
import GuideAppHeader from '@/components/guide/GuideAppHeader';
import { getResultByCategoryAndKey } from '@/pages/YangbokOrg/result/data/categoryResultTypes';
import type { ResultType } from '@/pages/YangbokOrg/questionFormTypes';

const LEGACY_SHOP_ID_MAP: Record<string, string> = {
  v1: 'billionstailor',
  v2: 'fortungarment',
  v3: 'atelier',
  v4: 'heritztailor',
};

const CATEGORY_AREA_NAMES: Record<string, string[]> = {
  suit: ['강북', '인천', '강남', '경기', '부산', '수원', '고양', '광명', '대구', '대전', '원주', '전주', '창원', '천안'],
  jewelry: ['강북', '강남', '부산', '수원', '울산', '창원'],
  hanbok: ['강남', '부산', '수원', '강북', '인천'],
};

const AREA_NAME_GROUP_MAP: Record<string, Record<string, string[]>> = {
  suit: {
    서울: ['강북', '강남'],
    '서울 강남권': ['강남'],
    '서울 그 외': ['강북'],
    '경기 · 인천': ['인천', '경기', '수원', '고양', '광명'],
    기타: ['부산', '대구', '대전', '원주', '전주', '창원', '천안'],
  },
  jewelry: {
    서울: ['강북', '강남'],
    '서울 강남권': ['강남'],
    '서울 그 외': ['강북'],
    '경기 · 인천': ['수원'],
    기타: ['부산', '울산', '창원'],
  },
  hanbok: {
    서울: ['강남', '강북'],
    '서울 강남권': ['강남'],
    '서울 그 외': ['강북'],
    '경기 · 인천': ['인천', '수원'],
    기타: ['부산'],
  },
};

function getMappedAreaNames(categoryKey: string, areaName: string) {
  if (!areaName || areaName === '전체') return [];

  const availableAreaNames = CATEGORY_AREA_NAMES[categoryKey] || [];
  if (availableAreaNames.includes(areaName)) {
    return [areaName];
  }

  const mappedAreaNames = AREA_NAME_GROUP_MAP[categoryKey]?.[areaName] || [];
  return mappedAreaNames.filter((name) => availableAreaNames.includes(name));
}

function legacyResultToGuideResult(result: ResultType): GuideResultData {
  return {
    key: result.key,
    displayName: result.title,
    summary: result.summary,
    plannerInterpretation: result.typeExplanation.paragraphs.join(' '),
    recommendDirection: result.mainRecommendation.description,
    vendorIds: (result.matchedShopIds || []).map((id) => LEGACY_SHOP_ID_MAP[id] || id),
    keywords: [result.recommendation],
    styleProfile: result.styleBars.map((bar) => ({
      label: bar.label,
      value: bar.value ?? 0,
    })),
    recommendedCombo: result.mainRecommendation.title,
    situationalGuides: result.situationCards.map((card) => ({
      title: card.title,
      description: card.description,
    })),
    recommendation: result.recommendation,
    rarityPercent: result.rarityPercent,
    typeExplanation: result.typeExplanation,
    styleBars: result.styleBars,
    mainRecommendation: result.mainRecommendation,
    upgradeConditions: result.upgradeConditions,
    situationCards: result.situationCards,
    matchedShopIds: result.matchedShopIds,
  };
}

function buildResultData(category: string, resultKey: string, configResult?: GuideResultData) {
  const legacyResult = getResultByCategoryAndKey(category, resultKey);
  const legacyGuideResult =
    legacyResult && legacyResult.key === resultKey ? legacyResultToGuideResult(legacyResult) : null;

  if (legacyGuideResult) {
    return {
      ...configResult,
      ...legacyGuideResult,
      vendorIds: legacyGuideResult.vendorIds || configResult?.vendorIds,
    };
  }

  return configResult || null;
}

interface GuideResultViewProps {
  config: GuideConfig;
  resultData: GuideResultData;
  answers: Record<string, string[]>;
}

function GuideResultView({ config, resultData, answers }: GuideResultViewProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuit = config.categoryKey === 'suit';
  const isJewelry = config.categoryKey === 'jewelry';
  const isHanbok = config.categoryKey === 'hanbok';
  const [shops, setShops] = useState<PartnerShopListItem[]>([]);
  const [shopLoading, setShopLoading] = useState(false);

  const categoryLabel = isSuit ? '예복' : isJewelry ? '예물' : isHanbok ? '한복' : '스타일';
  const storageKey = `guide:${config.categoryKey}`;
  const areaName = searchParams.get('areaName') || '';
  const areaQueryString = areaName ? `?${new URLSearchParams({ areaName }).toString()}` : '';
  const areaNames = useMemo(() => {
    return getMappedAreaNames(config.categoryKey, areaName);
  }, [areaName, config.categoryKey]);

  const handleRetry = () => {
    try {
      localStorage.removeItem(`${storageKey}:answers`);
      localStorage.removeItem(`${storageKey}:result`);
    } catch {
      // ignore
    }
    navigate(`/guide/${config.categoryKey}`);
  };

  const upgradeGuide: UpgradeGuideData | null = isSuit ? (suitUpgradeGuides[resultData.key] ?? null) : null;
  const upgradeResult = isSuit ? getUpgradeResult(resultData.key, config.results) : null;
  const checkPoints: string[] = isSuit ? (suitResultCheckPoints[resultData.key] ?? []) : [];
  const typeExplanation = resultData.typeExplanation;
  const styleItems = resultData.styleBars?.length
    ? resultData.styleBars.map((bar) => ({
        label: bar.label,
        value: bar.value ?? 0,
        level: bar.level,
      }))
    : (resultData.styleProfile || []).map((bar) => ({
        label: bar.label,
        value: bar.value,
        level: undefined,
      }));
  const mainRecommendation = resultData.mainRecommendation || {
    title: resultData.recommendedCombo || resultData.recommendation || '추천 방향',
    description: resultData.recommendDirection,
    reasons: checkPoints,
  };
  const upgradeConditions = resultData.upgradeConditions || upgradeGuide?.conditions || [];
  const situationGuides = resultData.situationCards?.length
    ? resultData.situationCards
    : (resultData.situationalGuides || []).map((guide, index) => ({
        ...guide,
        tag: index === 0 ? '현재 가장 잘 맞는 방향' : undefined,
        highlight: index === 0,
      }));

  const paragraphs = typeExplanation?.paragraphs?.length
    ? typeExplanation.paragraphs
    : resultData.plannerInterpretation
        .split(/[.。] /)
        .filter((s) => s.trim().length > 0)
        .map((s) => (s.endsWith('.') || s.endsWith('。') ? s : s + '.'));

  const fetchRecommendedShops = useCallback(async () => {
    setShopLoading(true);

    try {
      const { data } = await getPartnerShopList({
        categoryKey: config.categoryKey,
        page: 1,
        rows: 3,
        top3: 1,
        areaNames,
      });

      setShops(data.list || []);
    } catch (error) {
      console.error('[GuideResultView] failed to fetch recommended shops', error);
      setShops([]);
    } finally {
      setShopLoading(false);
    }
  }, [areaNames, config.categoryKey]);

  useEffect(() => {
    fetchRecommendedShops();
  }, [fetchRecommendedShops]);

  return (
    <div className="min-h-dvh flex flex-col bg-[#FAF7F2]">
      <GuideAppHeader step="result" title={config.headerTitle} onRetry={handleRetry} />

      <div className="flex-1 max-w-[720px] mx-auto w-full">
        {/* ═══════ 1. Result Hero ═══════ */}
        <div className="bg-gradient-to-b from-[#F5EDE3] via-[#F8F2EA] to-[#FAF7F2] px-5 md:px-8 pt-24 pb-10 md:pt-24 md:pb-14">
          <div className="text-center">
            <span className="inline-block px-3.5 py-1 rounded-full bg-[#EDE0D2] text-[#8A5A2B] text-[12px] md:text-[13px] font-medium tracking-wide mb-6">
              당신의 {categoryLabel} 성향
            </span>

            <h1 className="text-[32px] md:text-[40px] text-[#211C18] font-bold leading-[1.2] mb-4 tracking-tight">
              &ldquo;{resultData.displayName}&rdquo;
            </h1>

            {typeof resultData.rarityPercent === 'number' && (
              <p className="text-[12px] md:text-[13px] text-[#8A5A2B] font-medium mb-3">
                같은 성향의 신랑님 약 {resultData.rarityPercent}%
              </p>
            )}

            <p className="text-[15px] md:text-[16px] text-[#5F5953] max-w-[420px] mx-auto leading-relaxed mb-6">
              {resultData.summary}
            </p>

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#B4563F] text-white text-[12px] md:text-[13px] font-semibold">
                <i className="ri-compass-3-line text-[14px]"></i>
                추천 방향
              </span>
              {(resultData.keywords || []).map((kw) => (
                <span
                  key={kw}
                  className="inline-block px-3.5 py-2 rounded-full bg-white/80 text-[#3F3A35] text-[12px] md:text-[13px] font-medium border border-[#E1DBD4]"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 md:px-8 pb-12 md:pb-16">
          <div className="flex flex-col">
            {/* ═══════ 2. 성향 해석 ═══════ */}
            <section className="mb-10 md:mb-12">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-1 h-5 rounded-full bg-[#B4563F]"></div>
                <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">성향 해석</h2>
              </div>
              <div className="space-y-4 pl-3.5">
                {typeExplanation?.summary && (
                  <p className="text-[15px] md:text-[16px] text-[#211C18] font-semibold leading-[1.7]">
                    {typeExplanation.summary}
                  </p>
                )}
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-[14px] md:text-[15px] text-[#3F3A35] leading-[1.75]">
                    {p}
                  </p>
                ))}
              </div>
            </section>

            {/* ═══════ 3. 선호 스타일 바 ═══════ */}
            {styleItems.length > 0 && (
              <section className="mb-10 md:mb-12">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-1 h-5 rounded-full bg-[#B4563F]"></div>
                  <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">선호 스타일</h2>
                </div>
                <div className="rounded-2xl bg-white p-5 md:p-6 space-y-5">
                  {styleItems.map((bar) => (
                    <div key={bar.label}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[13px] md:text-[14px] text-[#3F3A35] font-medium">{bar.label}</span>
                        {bar.level && (
                          <span className="text-[12px] md:text-[13px] text-[#8A8279] font-medium">{bar.level}</span>
                        )}
                      </div>
                      <div className="w-full h-2.5 rounded-full bg-[#F2EDE5] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#B4563F] transition-all duration-800 ease-out"
                          style={{ width: `${bar.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ═══════ 4. 추천 결론 (강조 카드) ═══════ */}
            <section className="mb-10 md:mb-12">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-1 h-5 rounded-full bg-[#B4563F]"></div>
                <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">추천 결론</h2>
              </div>

              <div className="rounded-2xl bg-white border-l-[4px] border-l-[#B4563F] p-5 md:p-6">
                <p className="text-[12px] md:text-[13px] text-[#8A5A2B] font-medium mb-1.5 tracking-wide">
                  당신에게 가장 현실적인 추천
                </p>

                {mainRecommendation.title && (
                  <p className="text-[18px] md:text-[20px] text-[#211C18] font-bold mb-3 leading-snug">
                    {mainRecommendation.title}
                  </p>
                )}

                <p className="text-[14px] md:text-[15px] text-[#3F3A35] leading-relaxed mb-5">
                  {mainRecommendation.description}
                </p>

                {mainRecommendation.reasons.length > 0 && (
                  <div className="rounded-xl bg-[#F8F1EA] p-4 space-y-2.5">
                    <p className="text-[12px] md:text-[13px] text-[#8A5A2B] font-semibold mb-1">추천 이유</p>
                    {mainRecommendation.reasons.map((cp, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className="w-[18px] h-[18px] rounded-full bg-[#B4563F] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </div>
                        <span className="text-[13px] md:text-[14px] text-[#3F3A35] leading-relaxed">{cp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ═══════ 5. 업그레이드 가이드 (suit only) ═══════ */}
            {isSuit && upgradeConditions.length > 0 && (
              <section className="mb-10 md:mb-12">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-1 h-5 rounded-full bg-[#C8945C]"></div>
                  <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">
                    {upgradeGuide?.title || '업그레이드를 고려할 조건'}
                  </h2>
                </div>

                <div className="rounded-2xl bg-white border border-[#E8DFD3] p-5 md:p-6">
                  <p className="text-[14px] md:text-[15px] text-[#3F3A35] leading-relaxed mb-4">
                    {upgradeGuide?.description || '아래 조건에 해당한다면 한 단계 높은 선택지도 함께 비교해보세요.'}
                  </p>

                  <div className="space-y-3 mb-4">
                    {upgradeConditions.map((cond, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className="w-[18px] h-[18px] rounded-full bg-[#F8F1EA] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#C8945C] text-[10px] font-bold">{i + 1}</span>
                        </div>
                        <span className="text-[13px] md:text-[14px] text-[#3F3A35] leading-relaxed">{cond}</span>
                      </div>
                    ))}
                  </div>

                  {upgradeResult && (
                    <div className="pt-3 border-t border-[#E8DFD3] flex items-center flex-wrap gap-x-3 gap-y-1">
                      <span className="text-[12px] md:text-[13px] text-[#8A5A2B] font-medium">한 단계 위:</span>
                      <span className="text-[14px] md:text-[15px] text-[#211C18] font-semibold">{(upgradeResult as GuideResultData).displayName}</span>
                      <span className="text-[12px] md:text-[13px] text-[#8A8279]">· {(upgradeResult as GuideResultData).summary.slice(0, 45)}...</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ═══════ 6. 상황별 선택 가이드 ═══════ */}
            {situationGuides.length > 0 && (
              <section className="mb-10 md:mb-12">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-1 h-5 rounded-full bg-[#B4563F]"></div>
                  <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">상황별 선택 가이드</h2>
                </div>

                <div className="space-y-3">
                  {situationGuides.map((guide, i) => (
                    <div
                      key={i}
                      className={`rounded-2xl p-4 md:p-5 ${
                        guide.highlight || i === 0
                          ? 'bg-white border border-[#E1DBD4]'
                          : 'bg-white/60 border border-[#E8DFD3]/70'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            guide.highlight || i === 0 ? 'bg-[#B4563F]' : 'bg-[#F2EDE5]'
                          }`}
                        >
                          <span
                            className={`text-[12px] font-bold ${
                              guide.highlight || i === 0 ? 'text-white' : 'text-[#8A8279]'
                            }`}
                          >
                            {i + 1}
                          </span>
                        </div>
                        <div>
                          {guide.tag && (
                            <span className="inline-block text-[11px] text-[#8A5A2B] font-medium mb-1">
                              {guide.tag}
                            </span>
                          )}
                          <h3 className={`text-[14px] md:text-[15px] font-semibold mb-1 ${
                            guide.highlight || i === 0 ? 'text-[#211C18]' : 'text-[#3F3A35]'
                          }`}>
                            {guide.title}
                          </h3>
                          <p className="text-[13px] md:text-[14px] text-[#5F5953] leading-relaxed">
                            {guide.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ═══════ 7. 추천 인증업체 ═══════ */}
            {(shopLoading || shops.length > 0) && (
              <section className="mb-10 md:mb-12">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <div className="w-1 h-5 rounded-full bg-[#B4563F]"></div>
                  <h2 className="text-[16px] md:text-[17px] font-semibold text-[#211C18]">
                    먼저 비교해볼 만한 인증업체
                  </h2>
                </div>
                <p className="text-[13px] md:text-[14px] text-[#8A8279] mb-5 pl-3.5">
                  {areaName
                    ? `${areaName} 지역에서 이 타입이라면 아래 업체부터 확인해보세요.`
                    : '이 타입이라면 아래 업체부터 확인해보세요.'}
                </p>

                <div className="-mx-5 md:mx-0">
                  <div
                    className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-5 md:flex-col md:overflow-visible md:snap-none md:px-0 md:gap-4 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {shopLoading && shops.length === 0 && [1, 2, 3].map((value) => (
                      <div
                        key={value}
                        className="rounded-2xl bg-white border border-[#E1DBD4] overflow-hidden snap-start shrink-0 w-[85vw] md:w-auto animate-pulse"
                      >
                        <div className="flex flex-col md:flex-row">
                          <div className="w-full md:w-[260px] aspect-[16/9] md:aspect-[4/3] bg-[#F2EDE5] flex-shrink-0" />
                          <div className="p-4 md:p-5 flex-1">
                            <div className="h-4 bg-[#F2EDE5] rounded w-1/3 mb-3" />
                            <div className="h-3 bg-[#F2EDE5] rounded w-24 mb-3" />
                            <div className="h-3 bg-[#F2EDE5] rounded w-full mb-2" />
                            <div className="h-3 bg-[#F2EDE5] rounded w-2/3 mb-4" />
                            <div className="flex gap-2">
                              <div className="h-5 bg-[#F2EDE5] rounded-full w-14" />
                              <div className="h-5 bg-[#F2EDE5] rounded-full w-16" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {shops.map((shop) => {
                      const displayName = shop.displayName || shop.shopName;
                      const imageUrl = shop.heroThumbnailUrl || shop.heroImageUrl || '';
                      const location = [shop.areaName, shop.branchName].filter(Boolean).join(' · ');
                      const photoCount = Number(shop.reviewPhotoCount || 0) + Number(shop.lookbookPhotoCount || 0);
                      const tags = shop.keywords.slice(0, 3);

                      return (
                        <Link
                          key={shop.id}
                          to={`/detail/${shop.shopSlug}`}
                          className="group rounded-2xl bg-white border border-[#E1DBD4] overflow-hidden hover:border-[#D1C9BD] transition-colors cursor-pointer snap-start shrink-0 w-[85vw] md:w-auto"
                        >
                          <div className="flex flex-col md:flex-row">
                            <div className="relative w-full md:w-[260px] aspect-[16/9] md:aspect-[4/3] overflow-hidden bg-[#F2EDE5] flex-shrink-0 md:rounded-l-2xl">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={displayName}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <i className="ri-building-line text-[#C8BFB5] text-3xl"></i>
                                </div>
                              )}
                              <span className="absolute top-3 left-3 inline-block px-2.5 py-0.5 rounded-full bg-[#B4563F]/90 backdrop-blur-sm text-white text-[11px] font-medium">
                                {shop.shopCategory}
                              </span>
                            </div>

                            <div className="p-4 md:p-5 flex-1 min-w-0 flex flex-col justify-center">
                              <div className="flex items-start justify-between gap-3 mb-0.5">
                                <h3 className="text-[15px] md:text-[16px] font-bold text-[#211C18] group-hover:text-[#B4563F] transition-colors">
                                  {displayName}
                                </h3>
                                <span className="text-[11px] md:text-[12px] text-[#8A8279] whitespace-nowrap flex-shrink-0 mt-0.5">
                                  <i className="ri-image-line text-[11px] mr-1"></i>
                                  사진 {photoCount.toLocaleString()}장
                                </span>
                              </div>

                              <p className="text-[12px] md:text-[13px] text-[#8A8279] mb-2">
                                {location || shop.shopCategory}
                              </p>

                              <p className="text-[13px] md:text-[14px] text-[#3F3A35] leading-relaxed mb-2.5 line-clamp-2">
                                {shop.shortDescription}
                              </p>

                              {tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2.5">
                                  {tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-block px-2.5 py-0.5 rounded-full bg-[#F8F1EA] text-[#8A5A2B] text-[11px] font-medium"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {shop.reviewSummaryText && (
                                <>
                                  <p className="text-[12px] font-medium text-[#8A8279] mb-1">후기 요약</p>
                                  <p className="text-[13px] md:text-[14px] font-medium text-[#3F3A35] mb-1.5 line-clamp-1 leading-[1.5]">
                                    &ldquo;{shop.reviewSummaryText}&rdquo;
                                  </p>
                                </>
                              )}

                              <p className="text-[12px] text-[#8A8279]">
                                실제 후기 {shop.reviewCount.toLocaleString()}개 &middot; 직접 후기 {shop.directReviewCount.toLocaleString()}개
                              </p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* ═══════ 8. 하단 CTA ═══════ */}
            <div className="flex flex-col gap-3 pt-2">
              <Link
                to={`/shops/${config.categoryKey}${areaQueryString}`}
                className="inline-flex items-center justify-center gap-2 w-full h-[50px] rounded-full bg-[#B4563F] text-white text-[15px] font-semibold hover:bg-[#A04A35] transition-colors whitespace-nowrap cursor-pointer"
              >
                인증업체 전체 보기
                <i className="ri-arrow-right-line"></i>
              </Link>
              <button
                onClick={handleRetry}
                className="inline-flex items-center justify-center gap-1.5 w-full h-[46px] rounded-full border border-[#E1DBD4] text-[#3F3A35] text-[14px] font-medium hover:bg-white/80 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                다시 진단하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function GuideCategoryResultPage() {
  const { category, resultKey } = useParams<{ category: string; resultKey: string }>();
  const navigate = useNavigate();

  const [resultData, setResultData] = useState<GuideResultData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!category) {
      navigate('/guide', { replace: true });
      return;
    }

    const config = getGuideConfig(category);

    if (!config || !config.enabled) {
      navigate('/guide', { replace: true });
      return;
    }

    if (!resultKey) {
      navigate(`/guide/${category}`, { replace: true });
      return;
    }

    const found = buildResultData(
      category,
      resultKey,
      config.results.find((r) => r.key === resultKey)
    );

    if (!found) {
      navigate(`/guide/${category}`, { replace: true });
      return;
    }

    setResultData(found);

    try {
      const saved = localStorage.getItem(`guide:${category}:answers`);
      if (saved) {
        setAnswers(JSON.parse(saved));
      }
    } catch {
      // ignore
    }

    setLoading(false);
  }, [category, resultKey, navigate]);

  if (!category) return null;

  const config = getGuideConfig(category);
  if (!config || !config.enabled) return null;

  if (loading || !resultData) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-bg-alt">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-fill-normal flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-primary-normal text-xl animate-spin"></i>
          </div>
          <p className="text-[14px] text-label-assistive">결과를 불러오고 있어요</p>
        </div>
      </div>
    );
  }

  return <GuideResultView config={config} resultData={resultData} answers={answers} />;
}
